import { fetchAIOptions, fetchAISettings, updateAISettings } from '@/api/ai'
import {
  FALLBACK_AI_OPTIONS,
  getDefaultModelForProvider,
  getProviderOptions,
  resolveModelLabel,
  resolveProviderLabel,
} from '@/lib/aiOptions'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { CollapseSection } from '@/components/ui/CollapseSection'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useEffect, useMemo, useState } from 'react'

export function AISettingsCard({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [saved, setSaved] = useState(null)
  const [options, setOptions] = useState(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const [apiConfigOpen, setApiConfigOpen] = useState(false)
  const [playbooksOpen, setPlaybooksOpen] = useState(false)
  const [generalPlaybookOpen, setGeneralPlaybookOpen] = useState(false)
  const [threatdownPlaybookOpen, setThreatdownPlaybookOpen] = useState(false)
  const [eventLogsPlaybookOpen, setEventLogsPlaybookOpen] = useState(false)
  const [deviceHealthPlaybookOpen, setDeviceHealthPlaybookOpen] = useState(false)

  const [provider, setProvider] = useState('openai')
  const [model, setModel] = useState('gpt-4o-mini')
  const [baseUrl, setBaseUrl] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [generalPlaybook, setGeneralPlaybook] = useState('')
  const [threatdownPlaybook, setThreatdownPlaybook] = useState('')
  const [eventLogsPlaybook, setEventLogsPlaybook] = useState('')
  const [deviceHealthPlaybook, setDeviceHealthPlaybook] = useState('')
  const [defaults, setDefaults] = useState({
    general: '',
    threatdown: '',
    eventLogs: '',
    deviceHealth: '',
  })

  const providerOptions = useMemo(() => getProviderOptions(options), [options])
  const selectedProvider = useMemo(
    () => providerOptions.find((item) => item.id === provider) ?? null,
    [provider, providerOptions],
  )
  const modelChoices = selectedProvider?.models ?? []

  const savedProviderLabel = saved ? resolveProviderLabel(options, saved.provider) : ''
  const savedModelLabel = saved ? resolveModelLabel(options, saved.provider, saved.model) : ''
  const draftProviderLabel = resolveProviderLabel(options, provider)
  const draftModelLabel = resolveModelLabel(options, provider, model)

  const aiReady = Boolean(saved?.api_key_configured && saved?.enabled)
  const connectionConfigured = Boolean(saved?.api_key_configured)

  const hasUnsavedChanges = saved
    ? saved.provider !== provider
      || saved.model !== model
      || (saved.base_url || '') !== baseUrl
      || saved.enabled !== enabled
      || saved.general_playbook !== generalPlaybook
      || saved.threatdown_detections_playbook !== threatdownPlaybook
      || saved.device_event_logs_playbook !== eventLogsPlaybook
      || saved.device_health_playbook !== deviceHealthPlaybook
    : false

  useEffect(() => {
    Promise.all([
      fetchAISettings(),
      fetchAIOptions().catch(() => FALLBACK_AI_OPTIONS),
    ])
      .then(([settingsData, optionsData]) => {
        setSaved(settingsData)
        setOptions(optionsData)
        setProvider(settingsData.provider || 'openai')
        setModel(settingsData.model || 'gpt-4o-mini')
        setBaseUrl(settingsData.base_url || '')
        setEnabled(Boolean(settingsData.enabled))
        setGeneralPlaybook(settingsData.general_playbook || '')
        setThreatdownPlaybook(settingsData.threatdown_detections_playbook || '')
        setEventLogsPlaybook(settingsData.device_event_logs_playbook || '')
        setDeviceHealthPlaybook(settingsData.device_health_playbook || '')
        setDefaults({
          general: settingsData.general_playbook_default || '',
          threatdown: settingsData.threatdown_detections_playbook_default || '',
          eventLogs: settingsData.device_event_logs_playbook_default || '',
          deviceHealth: settingsData.device_health_playbook_default || '',
        })
      })
      .catch(() => setError('Could not load AI settings.'))
      .finally(() => setLoading(false))
  }, [])

  function handleProviderChange(nextProvider) {
    setProvider(nextProvider)
    const nextProviderOption = providerOptions.find((item) => item.id === nextProvider)
    if (!nextProviderOption?.models.length) {
      setModel('')
      return
    }
    const currentValid = nextProviderOption.models.some((item) => item.id === model)
    if (!currentValid) {
      setModel(getDefaultModelForProvider(options, nextProvider))
    }
  }

  async function handleSave(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const payload = {
        provider,
        model,
        base_url: baseUrl.trim() || null,
        enabled,
        general_playbook: generalPlaybook.trim(),
        threatdown_detections_playbook: threatdownPlaybook.trim(),
        device_event_logs_playbook: eventLogsPlaybook.trim(),
        device_health_playbook: deviceHealthPlaybook.trim(),
      }
      if (apiKey.trim()) payload.api_key = apiKey.trim()

      const updated = await updateAISettings(payload)
      setSaved(updated)
      setProvider(updated.provider)
      setModel(updated.model)
      setBaseUrl(updated.base_url || '')
      setEnabled(Boolean(updated.enabled))
      setGeneralPlaybook(updated.general_playbook)
      setThreatdownPlaybook(updated.threatdown_detections_playbook)
      setEventLogsPlaybook(updated.device_event_logs_playbook)
      setDeviceHealthPlaybook(updated.device_health_playbook)
      setDefaults({
        general: updated.general_playbook_default,
        threatdown: updated.threatdown_detections_playbook_default,
        eventLogs: updated.device_event_logs_playbook_default,
        deviceHealth: updated.device_health_playbook_default,
      })
      setApiKey('')
      setMessage('AI settings saved.')
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save AI settings.')
    } finally {
      setSaving(false)
    }
  }

  function restorePlaybookDefault(target) {
    if (target === 'general') {
      setGeneralPlaybook(defaults.general)
    } else if (target === 'threatdown') {
      setThreatdownPlaybook(defaults.threatdown)
    } else if (target === 'deviceHealth') {
      setDeviceHealthPlaybook(defaults.deviceHealth)
    } else {
      setEventLogsPlaybook(defaults.eventLogs)
    }
  }

  if (loading) return <Skeleton className="h-96 w-full" />

  const connectionPill = aiReady
    ? 'Active'
    : connectionConfigured
      ? 'Check model settings'
      : 'API key required'

  const assistantPill = !connectionConfigured
    ? 'Not ready'
    : hasUnsavedChanges && enabled !== saved?.enabled
      ? enabled
        ? 'Will enable on save'
        : 'Will disable on save'
      : saved?.enabled
        ? 'Enabled'
        : 'Disabled'

  return (
    <div className="space-y-4">
      {!embedded ? (
        <Card>
          <CardHeader>
            <CardTitle>AI assistant</CardTitle>
            <CardDescription>
              Configure the LLM used for natural-language queries in Bekat Dash. Customise
              instructions in the Playbook for each query area.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {saved ? (
        <Card className="space-y-4 p-[18px]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold">AI ready</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {aiReady
                  ? `${savedProviderLabel} · ${savedModelLabel} · API key saved`
                  : connectionConfigured
                    ? 'API key is saved. Check the provider and model are valid.'
                    : 'Add an API key below to activate AI across the portal.'}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
              {aiReady ? 'AI ready' : 'Not ready'}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <article className="rounded-lg border border-border bg-white p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">Connection</h3>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  {connectionPill}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">One provider, model, and API key for all AI features.</p>
            </article>

            <article className="rounded-lg border border-border bg-white p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">Assistant page</h3>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  {assistantPill}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Optional. Uses the same API key; turn on in the Playbooks section when ready.
              </p>
            </article>

            <article className="rounded-lg border border-border bg-white p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">General playbook</h3>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  {saved.general_playbook !== defaults.general ? 'Custom' : 'Default'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Used on the Assistant page for estate-wide questions.</p>
            </article>

            <article className="rounded-lg border border-border bg-white p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">ThreatDown playbook</h3>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  {saved.threatdown_detections_playbook !== defaults.threatdown ? 'Custom' : 'Default'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Used on the ThreatDown Detections tab.</p>
            </article>

            <article className="rounded-lg border border-border bg-white p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">Event logs playbook</h3>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  {saved.device_event_logs_playbook !== defaults.eventLogs ? 'Custom' : 'Default'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Used by Analyse on a device Event Logs tab.</p>
            </article>

            <article className="rounded-lg border border-border bg-white p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">Device health playbook</h3>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  {saved.device_health_playbook !== defaults.deviceHealth ? 'Custom' : 'Default'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Used by Analyse device on each device page.</p>
            </article>
          </div>
        </Card>
      ) : null}

      {error && !saved ? <p className="text-sm text-destructive">{error}</p> : null}

      {saved ? (
        <div className="space-y-4">
          <CollapsePanel
            id="ai-status-panel"
            title="Current AI configuration"
            collapsedSummary={
              aiReady
                ? `${savedProviderLabel} · ${savedModelLabel}`
                : connectionConfigured
                  ? 'API key saved · check provider and model'
                  : 'Not configured'
            }
            open={statusOpen}
            onOpenChange={setStatusOpen}
            pill={aiReady ? 'AI ready' : 'Not ready'}
            pillVariant={aiReady ? 'ready' : 'pending'}
          >
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Provider</dt>
                <dd className="font-medium">{savedProviderLabel}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Model in use</dt>
                <dd className="font-medium">
                  {savedModelLabel}
                  {saved.model && savedModelLabel !== saved.model && (
                    <span className="ml-1 text-xs text-muted-foreground">({saved.model})</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">API key</dt>
                <dd>{saved.api_key_configured ? 'Configured' : 'Not configured'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Enabled</dt>
                <dd>{saved.enabled ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">General playbook</dt>
                <dd>{saved.general_playbook !== defaults.general ? 'Custom' : 'Default'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">ThreatDown playbook</dt>
                <dd>
                  {saved.threatdown_detections_playbook !== defaults.threatdown ? 'Custom' : 'Default'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Event logs playbook</dt>
                <dd>{saved.device_event_logs_playbook !== defaults.eventLogs ? 'Custom' : 'Default'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Device health playbook</dt>
                <dd>{saved.device_health_playbook !== defaults.deviceHealth ? 'Custom' : 'Default'}</dd>
              </div>
            </dl>
            {!aiReady ? (
              <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {connectionConfigured
                  ? 'API key is saved, but AI will not run until provider and model are set correctly below.'
                  : 'Add a provider, model, and API key below, then click Save settings.'}
              </p>
            ) : null}
          </CollapsePanel>

          <form className="space-y-4" onSubmit={handleSave}>
            {hasUnsavedChanges ? (
              <p className="text-sm text-amber-600">You have unsaved changes.</p>
            ) : null}

            <CollapsePanel
              id="ai-api-panel"
              title="API and model"
              collapsedSummary={
                draftProviderLabel && draftModelLabel
                  ? `${draftProviderLabel} · ${draftModelLabel}`
                  : 'Provider, model, and API key'
              }
              open={apiConfigOpen}
              onOpenChange={setApiConfigOpen}
              pill={connectionPill}
              pillVariant={aiReady ? 'ready' : 'pending'}
            >
              <div className="max-w-lg space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-provider">Provider</Label>
                  <select
                    id="ai-provider"
                    className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                    value={provider}
                    onChange={(e) => handleProviderChange(e.target.value)}
                  >
                    {providerOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ai-model">Model</Label>
                  {modelChoices.length > 0 ? (
                    <select
                      id="ai-model"
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    >
                      {modelChoices.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id="ai-model"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      placeholder="Model identifier"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ai-base-url">Base URL (optional)</Label>
                  <Input
                    id="ai-base-url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="Leave blank for the provider default"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ai-api-key">
                    API key {saved?.api_key_configured ? '(leave blank to keep existing)' : ''}
                  </Label>
                  <Input
                    id="ai-api-key"
                    type="password"
                    autoComplete="off"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  {saved?.api_key_hint ? (
                    <p className="text-xs text-muted-foreground">Current key: {saved.api_key_hint}</p>
                  ) : null}
                </div>
              </div>
            </CollapsePanel>

            <CollapsePanel
              id="ai-playbooks-panel"
              title="Playbooks"
              collapsedSummary={
                saved.enabled
                  ? 'General · ThreatDown · Event logs · Device health · Enabled'
                  : enabled
                    ? 'Four playbooks · Assistant will enable on save'
                    : 'Four playbooks · Assistant disabled'
              }
              open={playbooksOpen}
              onOpenChange={setPlaybooksOpen}
              pill={assistantPill}
              pillVariant={saved.enabled ? 'ready' : 'pending'}
              accent="primary"
            >
              <CollapseSection
                title="General (Assistant)"
                subtitle="Estate-wide questions about devices, posture, patches, and ThreatDown coverage."
                open={generalPlaybookOpen}
                onOpenChange={setGeneralPlaybookOpen}
              >
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                      Use UK English. The <code className="rounded bg-muted px-1">{'{context}'}</code> placeholder
                      is replaced with portal data when a query runs.
                    </p>
                    <Button type="button" size="sm" variant="secondary" onClick={() => restorePlaybookDefault('general')}>
                      Restore default
                    </Button>
                  </div>
                  <textarea
                    id="ai-playbook-general"
                    rows={14}
                    className="flex w-full rounded-md border border-input bg-white px-3 py-2 font-mono text-xs leading-relaxed"
                    value={generalPlaybook}
                    onChange={(e) => setGeneralPlaybook(e.target.value)}
                  />
                </div>
              </CollapseSection>

              <CollapseSection
                title="ThreatDown detections"
                subtitle="Questions about detection events on the ThreatDown Detections tab."
                open={threatdownPlaybookOpen}
                onOpenChange={setThreatdownPlaybookOpen}
              >
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                      Use UK English. Required placeholder:{' '}
                      <code className="rounded bg-muted px-1">{'{context}'}</code>
                    </p>
                    <Button type="button" size="sm" variant="secondary" onClick={() => restorePlaybookDefault('threatdown')}>
                      Restore default
                    </Button>
                  </div>
                  <textarea
                    id="ai-playbook-threatdown"
                    rows={14}
                    className="flex w-full rounded-md border border-input bg-white px-3 py-2 font-mono text-xs leading-relaxed"
                    value={threatdownPlaybook}
                    onChange={(e) => setThreatdownPlaybook(e.target.value)}
                  />
                </div>
              </CollapseSection>

              <CollapseSection
                title="Device event logs"
                subtitle="On-demand diagnosis from the event-log snapshot stored for one device."
                open={eventLogsPlaybookOpen}
                onOpenChange={setEventLogsPlaybookOpen}
              >
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                      Use UK English. Required placeholder:{' '}
                      <code className="rounded bg-muted px-1">{'{context}'}</code>
                    </p>
                    <Button type="button" size="sm" variant="secondary" onClick={() => restorePlaybookDefault('eventLogs')}>
                      Restore default
                    </Button>
                  </div>
                  <textarea
                    id="ai-playbook-event-logs"
                    rows={14}
                    className="flex w-full rounded-md border border-input bg-white px-3 py-2 font-mono text-xs leading-relaxed"
                    value={eventLogsPlaybook}
                    onChange={(e) => setEventLogsPlaybook(e.target.value)}
                  />
                </div>
              </CollapseSection>

              <CollapseSection
                title="Device health"
                subtitle="Full-device health review from the Analyse device button on each device page."
                open={deviceHealthPlaybookOpen}
                onOpenChange={setDeviceHealthPlaybookOpen}
              >
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                      Use UK English. Required placeholder:{' '}
                      <code className="rounded bg-muted px-1">{'{context}'}</code>
                    </p>
                    <Button type="button" size="sm" variant="secondary" onClick={() => restorePlaybookDefault('deviceHealth')}>
                      Restore default
                    </Button>
                  </div>
                  <textarea
                    id="ai-playbook-device-health"
                    rows={14}
                    className="flex w-full rounded-md border border-input bg-white px-3 py-2 font-mono text-xs leading-relaxed"
                    value={deviceHealthPlaybook}
                    onChange={(e) => setDeviceHealthPlaybook(e.target.value)}
                  />
                </div>
              </CollapseSection>

              <div className="mt-4 border-t border-border pt-4">
                <label className="flex items-start gap-3 text-sm" htmlFor="ai-enabled">
                  <input
                    id="ai-enabled"
                    type="checkbox"
                    className="mt-1"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  <span>
                    <strong className="block font-medium">Enable AI assistant for portal users</strong>
                    <span className="text-muted-foreground">
                      When enabled, users can ask questions from the Assistant page, ThreatDown
                      detections tab, run device event-log analysis, and Analyse device health.
                    </span>
                  </span>
                </label>
              </div>
            </CollapsePanel>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save settings'}
              </Button>
              {message ? <p className="text-sm text-emerald-600">{message}</p> : null}
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}
